import { useState, useCallback, useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  RefreshCw,
  FileText,
  Table as TableIcon,
  Image,
  Eye,
  MapPin,
  Loader2,
  MoreHorizontal,
  FileDown,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";
import { ragApi, type SemanticChunk } from "@/services/api/rag";
import { SimplePDFViewer } from "@/components/portal/shared/SimplePDFViewer";

export const Route = createFileRoute(
  "/portal/knowledgebase-file/documents/$id",
)({
  component: DocumentDetailPage,
});

function DocumentDetailPage() {
  const { id } = Route.useParams();
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId || "";

  // Search, filter & sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [chunkTypeFilter, setChunkTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("chunk_order");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // PDF preview
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdf, setShowPdf] = useState(true);

  // Detail dialog
  const [selectedChunk, setSelectedChunk] = useState<SemanticChunk | null>(
    null,
  );

  // Load PDF presigned URL
  useEffect(() => {
    if (!token || !organisationId) return;
    setPdfLoading(true);
    ragApi
      .getDocumentDownloadUrl(token, id, organisationId)
      .then((res) => setPdfUrl(res.download_url))
      .catch(() => setPdfUrl(null))
      .finally(() => setPdfLoading(false));
  }, [token, organisationId, id]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const getSortDirection = (column: string): "asc" | "desc" | null => {
    return sortBy === column ? sortOrder : null;
  };

  // Server-side pagination
  const fetchChunks = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<SemanticChunk>> => {
      if (!token || !organisationId)
        return { data: [], totalCount: 0, page, pageSize };
      const response = await ragApi.getDocumentChunks(token, {
        document_id: id,
        search: searchTerm || undefined,
        chunk_type: chunkTypeFilter !== "all" ? chunkTypeFilter : undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: response.chunks,
        totalCount: response.total_chunks,
        page,
        pageSize,
      };
    },
    [
      token,
      organisationId,
      id,
      searchTerm,
      chunkTypeFilter,
      sortBy,
      sortOrder,
    ],
  );

  const {
    data: chunks,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<SemanticChunk>({
    fetchFn: fetchChunks,
    dependencies: [
      token,
      organisationId,
      id,
      searchTerm,
      chunkTypeFilter,
      sortBy,
      sortOrder,
    ],
  });

  const getChunkTypeBadge = (chunk: SemanticChunk) => {
    if (chunk.is_table) {
      return (
        <Badge variant="default" className="bg-purple-500">
          <TableIcon className="mr-1 h-3 w-3" />
          Table
        </Badge>
      );
    }
    if (chunk.is_visual) {
      return (
        <Badge variant="default">
          <Image className="mr-1 h-3 w-3" />
          Visual
        </Badge>
      );
    }
    return (
      <Badge variant="success">
        <FileText className="mr-1 h-3 w-3" />
        Text
      </Badge>
    );
  };

  const truncateContent = (content: string, maxLength = 120) => {
    if (content.length <= maxLength) return content;
    return content.slice(0, maxLength) + "...";
  };

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/portal/knowledgebase-file/documents"
          className="flex items-center gap-2 text-sm text-brand-blue-medium transition hover:text-brand-blue-dark"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Documents
        </Link>
      </div>

      {/* Header */}
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900">Document Chunks</h1>
          <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
            Document ID: {id}
          </p>
        </div>
      </div>

      {/* PDF Preview */}
      <div className="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden">
        <button
          onClick={() => setShowPdf((prev) => !prev)}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <FileDown className="h-4 w-4" />
            Document Preview
          </div>
          {showPdf ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {showPdf && (
          <div className="border-t h-[350px] md:h-[600px]">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : pdfUrl ? (
              <SimplePDFViewer url={pdfUrl} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-sm font-medium">Preview not available</p>
                <p className="text-xs mt-1">
                  The original file may not be stored in S3.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content card */}
      <div className="rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm space-y-4 md:space-y-6">
        {/* Controls bar */}
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search chunk content..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select
            value={chunkTypeFilter}
            onValueChange={(value) => setChunkTypeFilter(value)}
          >
            <SelectTrigger className="w-full md:w-[160px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="table">Table</SelectItem>
              <SelectItem value="figure">Figure</SelectItem>
              <SelectItem value="marginalia">Marginalia</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : chunks.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm || chunkTypeFilter !== "all"
                ? "No chunks match your filters"
                : "No chunks found"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || chunkTypeFilter !== "all"
                ? "Try adjusting your filters."
                : "This document has no extracted chunks."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("chunk_order")}
                  onSort={() => handleSort("chunk_order")}
                  className="w-[70px]"
                >
                  #
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("chunk_type")}
                  onSort={() => handleSort("chunk_type")}
                  className="w-[110px]"
                >
                  Type
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("page_number")}
                  onSort={() => handleSort("page_number")}
                  className="w-[80px]"
                >
                  Page
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                >
                  Content
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("chunk_size")}
                  onSort={() => handleSort("chunk_size")}
                  className="w-[90px] hidden lg:table-cell"
                >
                  Chars
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[100px] hidden lg:table-cell"
                >
                  Groundings
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[50px]"
                >
                  {" "}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chunks.map((chunk) => (
                <TableRow key={chunk.chunk_id}>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {chunk.chunk_order + 1}
                  </TableCell>
                  <TableCell>{getChunkTypeBadge(chunk)}</TableCell>
                  <TableCell className="text-sm">
                    {chunk.page_number || "-"}
                  </TableCell>
                  <TableCell className="max-w-[200px] md:max-w-md">
                    <div className="text-sm text-muted-foreground truncate">
                      {truncateContent(chunk.content)}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {chunk.chunk_size.toLocaleString()}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge variant="outline">
                      <MapPin className="mr-1 h-3 w-3" />
                      {chunk.groundings.length}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setSelectedChunk(chunk)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.totalCount > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              pageSizeOptions={pageSizeOptions}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={goToPage}
              onPageSizeChange={setPageSize}
              onFirstPage={firstPage}
              onLastPage={lastPage}
              onPrevPage={prevPage}
              onNextPage={nextPage}
            />
          </div>
        )}
      </div>

      {/* Chunk Detail Dialog */}
      <Dialog
        open={!!selectedChunk}
        onOpenChange={(open) => !open && setSelectedChunk(null)}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedChunk && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {getChunkTypeBadge(selectedChunk)}
                  <span>
                    Chunk {selectedChunk.chunk_order + 1}:{" "}
                    {selectedChunk.chunk_type}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {/* Content */}
                <div>
                  <h4 className="text-sm font-semibold mb-2">Content</h4>
                  {selectedChunk.is_table ? (
                    <div
                      className="bg-muted p-4 rounded-md overflow-x-auto text-sm"
                      dangerouslySetInnerHTML={{
                        __html: selectedChunk.content,
                      }}
                    />
                  ) : (
                    <div className="bg-muted p-4 rounded-md whitespace-pre-wrap text-sm max-h-[300px] overflow-y-auto">
                      {selectedChunk.content}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Groundings */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Groundings ({selectedChunk.groundings.length})
                  </h4>
                  {selectedChunk.groundings.length > 0 ? (
                    <div className="space-y-2">
                      {selectedChunk.groundings.map((grounding, idx) => (
                        <div
                          key={idx}
                          className="bg-muted/50 p-3 rounded-md text-sm space-y-1"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              Region {idx + 1}
                            </span>
                            <Badge variant="outline">
                              Page {grounding.page + 1}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            Left: {(grounding.box.left * 100).toFixed(1)}% |
                            Top: {(grounding.box.top * 100).toFixed(1)}% |
                            Right: {(grounding.box.right * 100).toFixed(1)}% |
                            Bottom: {(grounding.box.bottom * 100).toFixed(1)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No grounding information
                    </p>
                  )}
                </div>

                {/* Metadata */}
                {selectedChunk.metadata &&
                  Object.keys(selectedChunk.metadata).length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Metadata</h4>
                        <div className="bg-muted p-3 rounded-md">
                          <pre className="text-xs overflow-x-auto">
                            {JSON.stringify(selectedChunk.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </>
                  )}

                {/* Chunk Info */}
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Chunk ID:</span>
                    <div className="font-mono text-xs mt-1">
                      {selectedChunk.chunk_id}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Page:</span>
                    <div className="mt-1">{selectedChunk.page_number}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Characters:</span>
                    <div className="mt-1">
                      {selectedChunk.chunk_size.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Spans:</span>
                    <div className="mt-1 flex gap-2">
                      {selectedChunk.spans_multiple_pages && (
                        <Badge variant="outline">Multi-page</Badge>
                      )}
                      {selectedChunk.spans_multiple_regions && (
                        <Badge variant="outline">Multi-region</Badge>
                      )}
                      {!selectedChunk.spans_multiple_pages &&
                        !selectedChunk.spans_multiple_regions && (
                          <span className="text-muted-foreground">Single region</span>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
